import React, { useState, useRef, useEffect, useCallback } from "react";
import SidebarLayout from "../../common/sidebar/SidebarLayout";
import { Helmet } from "react-helmet";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import PatientLoader from "../../common/PatientLoader";
import OPSPrimaryHeader from "../../common/primaryHeader/OPSPrimaryHeader";
import AnalyticsFilters from "./components/AnalyticsFilters";
import HighRiskPatients from "./components/HighRiskPatients";
import TotalPatientsByCondition from "./components/TotalPatientsByCondition";
import ImprovementTracking from "./components/ImprovementTracking";
import WorseningSymptoms from "./components/WorseningSymptoms";
import PartialResponse from "./components/PartialResponse";
import RemissionTracking from "./components/RemissionTracking";
import MCIDImprovement from "./components/MCIDImprovement";
import MedianTimeToRemission from "./components/MedianTimeToRemission";
import AverageScoreTrend from "./components/charts/AverageScoreTrend";
import TotalAssessmentsChart from "./components/charts/TotalAssessmentsChart";
import SeverityDistPHQ9 from "./components/charts/SeverityDistPHQ9";
import SeverityDistGAD7 from "./components/charts/SeverityDistGAD7";
import PatientLevelTable from "./components/PatientLevelTable";
import PatientInfoModal from "./components/PatientInfoModal";
import { DigitalAnalyticsDashboards } from "./components/DigitalAnalyticsDashboards";
import { filterOptions } from "./mockData";
import {
  fetchAnalyticsCardsLogic,
  fetchClinicListLogic,
  fetchPatientListLogic,
  fetchAverageScoreTrendLogic,
  fetchTotalAssessmentsLogic,
  fetchSeverityDistributionLogic,
  fetchAnalyticsPhase2Logic,
} from "../../../store/features/analytics/analyticsSlice";
import "./AnalyticsPage.css";

const DownloadIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const TABS = [
  { id: "executive", label: "Executive Level" },
  { id: "patient", label: "Patient Level" },
  { id: "patient_engagement", label: "Patient Engagement" },
  { id: "onboarding_activation", label: "Patient Onboarding and Activation" },
  { id: "clinical_outcomes", label: "Clinical Outcomes" },
  { id: "assessment_analytics", label: "Assessment Analytics" },
];

const getFilterSummary = (filters, clinicList, patientList) => {
  const summary = [];
  if (filters.clinicIds?.length) {
    const list =
      clinicList?.clinics ||
      clinicList?.results ||
      (Array.isArray(clinicList) ? clinicList : []);
    const names = filters.clinicIds.map((id) => {
      const c = Array.isArray(list)
        ? list.find((cl) => String(cl.id || cl.clinic_id) === String(id))
        : null;
      return c ? c.name || c.clinic_name : `Clinic ${id}`;
    });
    summary.push({ label: "Clinic Name", value: names.join(", ") });
  }
  if (filters.patientIds?.length) {
    const allPatients = [];
    if (patientList?.mapped_patients)
      allPatients.push(...patientList.mapped_patients);
    if (patientList?.unmapped_patients?.precise_patients)
      allPatients.push(...patientList.unmapped_patients.precise_patients);
    if (patientList?.unmapped_patients?.healthie_patients)
      allPatients.push(...patientList.unmapped_patients.healthie_patients);
    const names = filters.patientIds.map((p) => {
      const pid = p.patient_id || p.healthie_patient_id;
      const match = allPatients.find(
        (pt) =>
          (pt.patient_id && pt.patient_id === p.patient_id) ||
          (pt.healthie_patient_id &&
            pt.healthie_patient_id === p.healthie_patient_id),
      );
      return match
        ? match.full_name ||
            [match.first_name, match.last_name].filter(Boolean).join(" ")
        : `Patient ${pid}`;
    });
    summary.push({ label: "Patient Name", value: names.join(", ") });
  }
  if (filters.assessmentTypes?.length) {
    const labels = filters.assessmentTypes.map(
      (v) =>
        filterOptions.assessmentTypes.find((o) => o.value === v)?.label || v,
    );
    summary.push({ label: "Assessment Type", value: labels.join(", ") });
  }
  if (filters.genders?.length) {
    const labels = filters.genders.map(
      (v) => filterOptions.genders.find((o) => o.value === v)?.label || v,
    );
    summary.push({ label: "Gender", value: labels.join(", ") });
  }
  if (filters.ageRange) {
    const label =
      filterOptions.ageRanges.find((o) => o.value === filters.ageRange)
        ?.label || filters.ageRange;
    summary.push({ label: "Age Range", value: label });
  }
  if (filters.ticStartDate && filters.ticEndDate) {
    const start = filters.ticStartDate.split("T")[0];
    const end = filters.ticEndDate.split("T")[0];
    summary.push({ label: "Time in Care", value: `${start}  to  ${end}` });
  }
  if (filters.startDate && filters.endDate) {
    const start = filters.startDate.split("T")[0];
    const end = filters.endDate.split("T")[0];
    summary.push({ label: "Date Range", value: `${start}  to  ${end}` });
  }
  return summary;
};

const buildFilterPayload = (filters = {}) => {
  const payload = {};

  if (filters.clinicIds?.length) {
    payload.clinic_id = filters.clinicIds.map((id) => Number(id));
  }
  if (filters.patientIds?.length) {
    payload.patient_id = filters.patientIds;
  }
  if (filters.assessmentTypes?.length) {
    payload.assessment_type = filters.assessmentTypes;
  }
  if (filters.genders?.length) {
    payload.gender = filters.genders;
  }
  if (filters.ageRange) {
    payload.age_range = filters.ageRange;
  }
  if (filters.ticStartDate) {
    payload.time_in_care_start_date = filters.ticStartDate.split("T")[0];
  }
  if (filters.ticEndDate) {
    payload.time_in_care_end_date = filters.ticEndDate.split("T")[0];
  }
  if (filters.startDate) {
    payload.start_date = filters.startDate.split("T")[0];
  }
  if (filters.endDate) {
    payload.end_date = filters.endDate.split("T")[0];
  }

  return payload;
};

const AnalyticsPage = () => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.app);
  const {
    cardsData,
    cardsLoading,
    clinicList,
    clinicListLoading,
    patientList,
    patientListLoading,
    averageScoreTrendData,
    averageScoreTrendLoading,
    totalAssessmentsData,
    totalAssessmentsLoading,
    severityDistributionData,
    severityDistributionLoading,
    phase2Data,
    phase2Loading,
  } = useSelector((state) => state.analytics);
  const [activeTab, setActiveTab] = useState("executive");
  const [isExporting, setIsExporting] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [loadingAction, setLoadingAction] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState(null);
  const analyticsContentRef = useRef(null);

  const handleCardClick = (config) => {
    setModalConfig(config);
    setModalOpen(true);
  };

  const isDataLoading =
    cardsLoading ||
    averageScoreTrendLoading ||
    totalAssessmentsLoading ||
    severityDistributionLoading ||
    phase2Loading;

  const isCurrentTabLoading =
    activeTab === "executive"
      ? cardsLoading ||
        averageScoreTrendLoading ||
        totalAssessmentsLoading ||
        severityDistributionLoading
      : activeTab === "patient"
      ? false
      : phase2Loading;

  const dispatchDataThunks = (filters = {}) => {
    const payload = buildFilterPayload(filters);
    dispatch(fetchAnalyticsCardsLogic(payload));
    dispatch(fetchAverageScoreTrendLogic(payload));
    dispatch(fetchTotalAssessmentsLogic(payload));
    dispatch(fetchSeverityDistributionLogic(payload));
    dispatch(fetchAnalyticsPhase2Logic(payload));
  };

  useEffect(() => {
    dispatch(fetchClinicListLogic({ page: 1, limit: 100, orderBy: "DESC" }));
    dispatch(fetchPatientListLogic({ page: 1, limit: 10000, orderBy: "DESC" }));
    dispatchDataThunks();
  }, []);

  useEffect(() => {
    if (!isDataLoading && loadingAction) {
      setLoadingAction(null);
    }
  }, [isDataLoading, loadingAction]);

  const handleClinicChange = (clinicIds) => {
    const params = { page: 1, limit: 10000, orderBy: "DESC" };
    if (Array.isArray(clinicIds) && clinicIds.length > 0) {
      params.clinic_ids = clinicIds.join(",");
    }
    dispatch(fetchPatientListLogic(params));
  };

  const handleApplyFilter = (filters) => {
    setAppliedFilters(filters);
    setLoadingAction("apply");
    dispatchDataThunks(filters);
  };

  const handleResetFilter = () => {
    setAppliedFilters({});
    setLoadingAction("reset");
    dispatchDataThunks();
  };

  const captureElement = useCallback(async (element, exportId) => {
    const scale = Math.max(2.5, window.devicePixelRatio || 1);
    element.setAttribute("data-pdf-export-id", exportId);
    try {
      const canvas = await html2canvas(element, {
        backgroundColor: "#ffffff",
        scale,
        useCORS: true,
        logging: false,
        windowWidth: document.documentElement.scrollWidth,
        windowHeight: document.documentElement.scrollHeight,
        onclone: (doc) => {
          const el = doc.querySelector(`[data-pdf-export-id="${exportId}"]`);
          if (!el) return;
          el.querySelectorAll(".analytics-chart-expand-btn").forEach((b) =>
            b.remove(),
          );
          el.querySelectorAll(".analytics-chart-scroll-wrapper").forEach(
            (w) => {
              w.style.overflowX = "visible";
              w.style.overflowY = "visible";
              w.style.maxHeight = "none";
              w.style.justifyContent = "flex-start";
            },
          );
          el.querySelectorAll(".analytics-chart-content").forEach((c) => {
            c.style.minWidth = "0";
            c.style.width = "100%";
            c.style.maxWidth = "100%";
            Array.from(c.children).forEach((child) => {
              child.style.minWidth = "0";
              child.style.width = "100%";
              child.style.maxWidth = "100%";
              child.style.overflow = "visible";
            });
          });
          el.querySelectorAll("canvas").forEach((cvs) => {
            const parent = cvs.parentElement;
            if (parent) {
              parent.style.width = "100%";
              parent.style.minWidth = "0";
              parent.style.maxWidth = "100%";
            }
          });
        },
        ignoreElements: (el) =>
          el.classList?.contains("analytics-chart-expand-btn") ||
          el.classList?.contains("analytics-fullscreen-modal") ||
          el.classList?.contains("analytics-loading-overlay") ||
          el.classList?.contains("analytics-info-btn"),
      });
      if (!canvas.width || !canvas.height) return null;
      return canvas.toDataURL("image/png", 1.0);
    } finally {
      element.removeAttribute("data-pdf-export-id");
    }
  }, []);

  const drawFilterSummary = useCallback(
    (pdf, yPos, margin, contentWidth) => {
      const summary = getFilterSummary(appliedFilters, clinicList, patientList);
      const hasFilters = summary.length > 0;

      const boxX = margin;
      const boxWidth = contentWidth;
      const lineHeight = 5.5;
      const padding = 6;
      const colGap = 4;
      const colWidth = (boxWidth - 2 * padding - colGap) / 2;

      const rows = [];
      for (let i = 0; i < summary.length; i += 2) {
        rows.push(summary.slice(i, i + 2));
      }

      const headerLine = 7;
      const contentLines = hasFilters
        ? rows.length * lineHeight + 2
        : lineHeight;
      const boxHeight = 2 * padding + headerLine + contentLines;

      pdf.setFillColor(241, 245, 249);
      pdf.setDrawColor(226, 232, 240);
      pdf.roundedRect(boxX, yPos, boxWidth, boxHeight, 3, 3, "FD");

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.setTextColor(30, 41, 59);
      pdf.text("Applied Filters", boxX + padding, yPos + padding + 4);

      if (!hasFilters) {
        pdf.setFont("helvetica", "italic");
        pdf.setFontSize(8.5);
        pdf.setTextColor(100, 116, 139);
        pdf.text(
          "No filters applied \u2014 showing all data",
          boxX + padding,
          yPos + padding + headerLine + 4,
        );
      } else {
        let textY = yPos + padding + headerLine + 2;
        rows.forEach((row) => {
          row.forEach((item, colIdx) => {
            const x = boxX + padding + colIdx * (colWidth + colGap);
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(8);
            pdf.setTextColor(71, 85, 105);
            pdf.text(`${item.label}:`, x, textY + 3);

            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(8);
            pdf.setTextColor(30, 41, 59);
            const labelWidth = pdf.getTextWidth(`${item.label}: `);
            const valueText = pdf.splitTextToSize(
              item.value,
              colWidth - labelWidth - 2,
            );
            pdf.text(valueText[0] || "", x + labelWidth, textY + 3);
          });
          textY += lineHeight;
        });
      }

      return yPos + boxHeight + 6;
    },
    [appliedFilters, clinicList, patientList],
  );

  const handleExportPDF = async () => {
    if (!analyticsContentRef.current) {
      toast.error("Cannot export PDF: Content not found");
      return;
    }

    setIsExporting(true);
    const toastId = toast.info("Generating PDF...", { autoClose: false });

    try {
      await new Promise((resolve) => setTimeout(resolve, 450));

      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pageWidth - 2 * margin;
      const colGap = 6;
      const rowGap = 6;
      const colWidth = (contentWidth - colGap) / 2;

      const ensureSpace = (needed) => {
        if (yPos + needed > pageHeight - margin) {
          pdf.addPage();
          yPos = margin;
        }
      };

      let yPos = margin;

      // --- Title ---
      const tabLabel =
        TABS.find((t) => t.id === activeTab)?.label || "Analytics";
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(16);
      pdf.setTextColor(30, 41, 59);
      pdf.text(`Analytics Report \u2014 ${tabLabel}`, margin, yPos + 6);
      yPos += 10;

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.setTextColor(100, 116, 139);
      const dateStr = new Date().toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      pdf.text(`Generated on ${dateStr}`, margin, yPos + 3);
      yPos += 8;

      // --- Filter summary ---
      yPos = drawFilterSummary(pdf, yPos, margin, contentWidth);

      const loadImgAspect = (imgData) =>
        new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve(img.width / img.height);
          img.src = imgData;
        });

      if (activeTab === "patient") {
        // --- Patient table ---
        const tableWrapper = analyticsContentRef.current.querySelector(
          ".patient-level-table-wrapper",
        );
        if (tableWrapper) {
          const tableImg = await captureElement(
            tableWrapper,
            "pdf-patient-table",
          );
          if (tableImg) {
            const aspect = await loadImgAspect(tableImg);
            const h = contentWidth / aspect;
            ensureSpace(h + rowGap);
            pdf.addImage(tableImg, "PNG", margin, yPos, contentWidth, h);
            yPos += h + rowGap;
          }
        }

        // --- Patient detail (if a patient is selected) ---
        const detailEl =
          analyticsContentRef.current.querySelector(".patient-detail");
        if (detailEl && detailEl.getBoundingClientRect().height > 0) {
          const detailImg = await captureElement(
            detailEl,
            "pdf-patient-detail",
          );
          if (detailImg) {
            const aspect = await loadImgAspect(detailImg);
            const h = contentWidth / aspect;
            ensureSpace(h + rowGap);
            pdf.addImage(detailImg, "PNG", margin, yPos, contentWidth, h);
            yPos += h + rowGap;
          }
        }
      } else {
        // --- Executive: Metric card sections ---
        const sections = Array.from(
          analyticsContentRef.current.querySelectorAll(".analytics-section"),
        ).filter((s) => {
          const r = s.getBoundingClientRect();
          return r.width > 0 && r.height > 0;
        });

        if (sections.length > 0) {
          const sectionImages = [];
          for (let i = 0; i < sections.length; i++) {
            const img = await captureElement(sections[i], `pdf-section-${i}`);
            if (img) {
              const isWide = !!sections[i].querySelector(
                ".analytics-card-grid-2x3",
              );
              sectionImages.push({ img, isWide });
            }
          }

          const normalSections = sectionImages.filter((s) => !s.isWide);
          const wideSections = sectionImages.filter((s) => s.isWide);

          const drawNormalPair = async (left, right) => {
            const aspectL = await loadImgAspect(left.img);
            const hL = colWidth / aspectL;
            if (right) {
              const aspectR = await loadImgAspect(right.img);
              const hR = colWidth / aspectR;
              ensureSpace(Math.max(hL, hR) + rowGap);
              pdf.addImage(left.img, "PNG", margin, yPos, colWidth, hL);
              pdf.addImage(
                right.img,
                "PNG",
                margin + colWidth + colGap,
                yPos,
                colWidth,
                hR,
              );
              yPos += Math.max(hL, hR) + rowGap;
            } else {
              ensureSpace(hL + rowGap);
              pdf.addImage(left.img, "PNG", margin, yPos, colWidth, hL);
              yPos += hL + rowGap;
            }
          };

          const drawWide = async (entry) => {
            const aspect = await loadImgAspect(entry.img);
            const h = contentWidth / aspect;
            ensureSpace(h + rowGap);
            pdf.addImage(entry.img, "PNG", margin, yPos, contentWidth, h);
            yPos += h + rowGap;
          };

          for (let i = 0; i < normalSections.length; i += 2) {
            await drawNormalPair(
              normalSections[i],
              normalSections[i + 1] || null,
            );
          }
          for (const wide of wideSections) {
            await drawWide(wide);
          }
        }

        // --- Executive: Chart cards (direct canvas capture) ---
        const chartCards = Array.from(
          analyticsContentRef.current.querySelectorAll(".analytics-chart-card"),
        ).filter((c) => {
          const r = c.getBoundingClientRect();
          return r.width > 0 && r.height > 0;
        });

        const chartEntries = [];
        for (const card of chartCards) {
          const title =
            card.querySelector(".analytics-chart-title")?.textContent || "";
          const subtitle =
            card.querySelector(".analytics-chart-period")?.textContent || "";
          const canvas = card.querySelector("canvas");
          if (!canvas || !canvas.width || !canvas.height) continue;
          chartEntries.push({
            title,
            subtitle,
            imageData: canvas.toDataURL("image/png"),
            aspect: canvas.width / canvas.height,
          });
        }

        const titleBlockHeight = 10;
        for (let i = 0; i < chartEntries.length; i += 2) {
          const pair = chartEntries.slice(i, i + 2);
          const imgHeights = pair.map((entry) => colWidth / entry.aspect);
          const rowHeight = Math.max(...imgHeights) + titleBlockHeight;
          ensureSpace(rowHeight + rowGap);

          pair.forEach((entry, colIdx) => {
            const x = margin + colIdx * (colWidth + colGap);
            const imgH = colWidth / entry.aspect;

            pdf.setFillColor(255, 255, 255);
            pdf.setDrawColor(226, 232, 240);
            pdf.roundedRect(
              x,
              yPos,
              colWidth,
              imgH + titleBlockHeight,
              2,
              2,
              "FD",
            );

            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(9);
            pdf.setTextColor(30, 41, 59);
            pdf.text(entry.title, x + 4, yPos + 5);

            if (entry.subtitle) {
              pdf.setFont("helvetica", "normal");
              pdf.setFontSize(7);
              pdf.setTextColor(100, 116, 139);
              const subtitleWidth = pdf.getTextWidth(entry.subtitle);
              pdf.text(
                entry.subtitle,
                x + colWidth - subtitleWidth - 4,
                yPos + 5,
              );
            }

            pdf.addImage(
              entry.imageData,
              "PNG",
              x + 1,
              yPos + titleBlockHeight,
              colWidth - 2,
              imgH,
            );
          });

          yPos += rowHeight + rowGap;
        }
      }

      const filename = `Analytics_${tabLabel.replace(/\s+/g, "_")}_${
        new Date().toISOString().split("T")[0]
      }.pdf`;
      pdf.save(filename);

      toast.dismiss(toastId);
      toast.success("PDF exported successfully!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.dismiss(toastId);
      toast.error("Failed to generate PDF. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <SidebarLayout>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Analytics | Precise Digital</title>
        <link rel="canonical" href={window.location.origin} />
      </Helmet>
      {loading ? (
        <PatientLoader />
      ) : (
        <div className="analytics-page">
          <div className="analytics-header-row">
            <OPSPrimaryHeader
              titleText="Analytics"
              showSubtitle={false}
              showButton={false}
            />
          </div>

          <AnalyticsFilters
            activeTab={activeTab}
            onApply={handleApplyFilter}
            onReset={handleResetFilter}
            clinicList={clinicList}
            clinicListLoading={clinicListLoading}
            patientList={patientList}
            patientListLoading={patientListLoading}
            onClinicChange={handleClinicChange}
            isDataLoading={isCurrentTabLoading}
            loadingAction={loadingAction}
          />

          <div className="analytics-tabs">
            <div className="analytics-tabs-left">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  className={`analytics-tab ${
                    activeTab === tab.id ? "analytics-tab--active" : ""
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="analytics-btn-export"
              onClick={handleExportPDF}
              disabled={isExporting}
            >
              {isExporting ? (
                <>
                  <span className="spinner" />
                  Exporting...
                </>
              ) : (
                <>
                  <DownloadIcon />
                  Export PDF
                </>
              )}
            </button>
          </div>

          <div ref={analyticsContentRef} className="analytics-content-wrapper">
            {isCurrentTabLoading && (
              <div className="analytics-loading-overlay">
                <div className="analytics-loading-spinner" />
                <span className="analytics-loading-text">
                  Loading analytics data...
                </span>
              </div>
            )}
            {activeTab === "executive" && (
              <div className="analytics-content">
                <div className="analytics-two-col">
                  <div className="analytics-col">
                    <HighRiskPatients
                      data={cardsData}
                      loading={cardsLoading}
                      onCardClick={handleCardClick}
                    />
                    <ImprovementTracking
                      data={cardsData}
                      loading={cardsLoading}
                      onCardClick={handleCardClick}
                    />
                    <PartialResponse
                      data={cardsData}
                      loading={cardsLoading}
                      onCardClick={handleCardClick}
                    />
                    <MedianTimeToRemission
                      data={cardsData}
                      loading={cardsLoading}
                      onCardClick={handleCardClick}
                    />
                  </div>
                  <div className="analytics-col">
                    <TotalPatientsByCondition
                      data={cardsData}
                      loading={cardsLoading}
                      onCardClick={handleCardClick}
                    />
                    <WorseningSymptoms
                      data={cardsData}
                      loading={cardsLoading}
                      onCardClick={handleCardClick}
                    />
                    <RemissionTracking
                      data={cardsData}
                      loading={cardsLoading}
                      onCardClick={handleCardClick}
                    />
                    <MCIDImprovement
                      data={cardsData}
                      loading={cardsLoading}
                      onCardClick={handleCardClick}
                    />
                  </div>
                </div>

                <div className="analytics-charts-grid">
                  <AverageScoreTrend
                    data={averageScoreTrendData}
                    loading={averageScoreTrendLoading}
                    onCardClick={handleCardClick}
                  />
                  <TotalAssessmentsChart
                    data={totalAssessmentsData}
                    loading={totalAssessmentsLoading}
                  />
                  <SeverityDistPHQ9
                    data={severityDistributionData}
                    loading={severityDistributionLoading}
                    onCardClick={handleCardClick}
                  />
                  <SeverityDistGAD7
                    data={severityDistributionData}
                    loading={severityDistributionLoading}
                    onCardClick={handleCardClick}
                  />
                </div>

                <PatientInfoModal
                  isOpen={modalOpen}
                  onClose={() => {
                    setModalOpen(false);
                    setModalConfig(null);
                  }}
                  cardConfig={modalConfig}
                  appliedFilters={appliedFilters}
                />
              </div>
            )}

            {activeTab === "patient" && (
              <div className="analytics-content">
                <PatientLevelTable filters={appliedFilters} />
              </div>
            )}

            {[
              "clinical_outcomes",
              "patient_engagement",
              "assessment_analytics",
              "onboarding_activation",
            ].includes(activeTab) && (
              <div className="analytics-content">
                <DigitalAnalyticsDashboards
                  activeTab={activeTab}
                  data={phase2Data}
                  loading={phase2Loading}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </SidebarLayout>
  );
};

export default AnalyticsPage;
